import { useContext } from 'react'
import { ArrayFieldStore } from '../Stores/ArrayFieldStore'
import { BaseFieldStore } from '../Stores/BaseFieldStore'
import { ObjectFieldStore } from '../Stores/ObjectFieldStore'
import { TableFieldStore } from '../Stores/TableFieldStore'
import { FormComposerContext } from './FormComposerContextProvider'
import { FormContext } from './FormContextProvider'

/**
 * Hook для получения поля формы по имени
 */
export const useFormField = (fieldName: string): BaseFieldStore | undefined => {
    const form = useContext(FormContext)
    return form?.getField(fieldName)
}

/**
 * Hook для получения поля-массива
 */
export const useArrayField = (fieldName: string): ArrayFieldStore | undefined => {
    const field = useFormField(fieldName)
    return field instanceof ArrayFieldStore ? field : undefined
}

/**
 * Hook для получения объектного поля
 */
export const useObjectField = (fieldName: string): ObjectFieldStore | undefined => {
    const field = useFormField(fieldName)
    return field instanceof ObjectFieldStore ? field : undefined
}

/**
 * Hook для получения табличного поля
 */
export const useTableField = (fieldName: string): TableFieldStore | undefined => {
    const field = useFormField(fieldName)
    return field instanceof TableFieldStore ? field : undefined
}

/**
 * Hook для получения значения поля
 */
export const useFieldValue = (fieldName: string): any => {
    const form = useContext(FormContext)
    return form?.getValue(fieldName)
}

/**
 * Hook для получения ошибки поля
 */
export const useFieldError = (fieldName: string): string | null => {
    const form = useContext(FormContext)
    return form?.getError(fieldName) || null
}

/**
 * Hook для проверки валидности поля
 */
export const useFieldValid = (fieldName: string): boolean => {
    const form = useContext(FormContext)
    const error = form?.getError(fieldName)
    return !error
}

/**
 * Hook для получения состояния формы
 */
export const useFormState = () => {
    const form = useContext(FormContext)
    if (!form) {
        throw new Error('useFormState must be used within a FormContextProvider')
    }

    return {
        isValid: form.isValid,
        isLoading: form.isLoading,
        isEditable: form.isEditable,
        values: form.values,
        errors: form.errors,
        submit: () => form.submit(),
        validate: () => form.validate(),
        reset: () => form.reset(),
        clear: () => form.clear(),
    }
}

/**
 * Hook для работы с композером форм
 */
export const useFormComposer = () => {
    const composer = useContext(FormComposerContext)
    if (!composer) {
        throw new Error('useFormComposer must be used within a FormComposerContextProvider')
    }

    return {
        forms: composer.forms,
        isValid: composer.isValid,
        isSubmitSendRequest: composer.isSubmitSendRequest,
        storages: composer.storages,
        setFormContext: (name: string, form: any) => composer.setFormContext(name, form),
        getFormContext: (name: string) => composer.getFormContext(name),
        clearFormContext: () => composer.clearFormContext(),
        submit: (name?: string | string[]) => composer.submit(name),
        validate: (name?: string | string[]) => composer.validate(name),
        getAllValues: () => composer.getAllValues(),
        getAllErrors: () => composer.getAllErrors(),
        resetAllForms: () => composer.resetAllForms(),
        clearAllForms: () => composer.clearAllForms(),
    }
}

/**
 * Hook для автоматического биндинга поля к React компонентам
 */
export const useFieldBinding = (fieldName: string, props: any = {}) => {
    const field = useFormField(fieldName)
    if (!field) {
        return {}
    }

    return field.bind(props)
}
